/**
 * Record page — list, filter, and manage orders / quote records under the
 * deployment's record noun.
 *
 * Route: /orders (gated by 'workflow_engine' since E4 — SA-389, spec D26).
 * Payment, production and delivery UI renders only with 'commerce'.
 */

import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { subscribeInboxEvents } from '@/services/inboxEventBus'
import { cn } from '@/utils'
import {
  ShoppingCart, Search, Package, AlertTriangle, RefreshCw, X, Plus, ClipboardList,
} from 'lucide-react'
import { Button, Spinner } from '@/components/ui'
import { RegisterColumnsSlot, RegisterViewSlot, moduleSorts, useRegisterViews } from '@/components/custom/CustomSlots'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageFrame } from '@/components/layout/PageFrame'
import { NewOrderModal } from '@/components/orders/NewOrderModal'
import { NewQuoteRecordModal } from '@/components/orders/NewQuoteRecordModal'
import { OrderDetail } from '@/components/orders/OrderDetail'
import { LegacyOrdersTable } from '@/components/orders/LegacyOrdersTable'
import { RegisterToolbar } from '@/components/records/RegisterToolbar'
import { DEFAULT_SORT, REGISTER_SORT_KEYS, columnSort, sortSurvives, type RegisterSort } from '@/components/records/registerSort'
import { RegisterFilterPanel } from '@/components/records/RegisterFilterPanel'
import {
  clearAllFilters, type RegisterFilterSetters, type RegisterFilterState, type StepOption,
} from '@/components/records/registerFilterModel'
import { ListPager } from '@/components/records/ListPager'
import { RecordViewSwitch } from '@/components/records/RecordViewSwitch'
import { TasksList, TaskScopeSwitch } from '@/pages/TasksPage'
import { STATUS_CONFIG, COMMERCE_LADDER_STATUSES } from '@/components/orders/orderStatusConfig'
import { ORDERS_PAGE_SIZE, useOrders } from '@/hooks/useOrders'
import { useCanAccessFeature, useFeature } from '@/hooks'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { useFeatureStore } from '@/stores/featureStore'
import { formatCurrency } from '@/utils/currency'
import { QUEUE_STATE_CONFIG } from '@/constants/queueStates'
import { LIST_PARAMS, listFromUrl } from '@/pages/ordersListUrl'
import { useBackLanding } from '@/pages/tasks/backLanding'
import type {
  Order, OrderStatus, OrderStats, OrderQueueStats, OrderUpdatePayload, QuoteOutcome, IntakeAgenda,
} from '@/types/commerce'
import type { TaskScope } from '@/types/process'
import { tourAnchor } from '@/components/tour/tourAnchor'

// ── Stats Bar ────────────────────────────────────────────────────────

/** E4 (SA-389, spec D26): the payment / production / revenue tiles are
 * commerce; Total and Needs Quote belong to every record deployment. */
function StatsBar({ stats, showCommerce }: { stats: OrderStats | null; showCommerce: boolean }) {
  if (!stats) return null
  const tiles: Array<{ label: string; value: number | null; color: string; display?: string }> = [
    { label: 'Total', value: stats.total, color: 'text-muted-foreground' },
    { label: 'Needs Quote', value: stats.pendingQuote, color: 'text-warning-strong' },
    ...(showCommerce ? [
      { label: 'Awaiting Payment', value: stats.pendingPayment, color: 'text-accent-orange' },
      { label: 'In Production', value: stats.inProduction, color: 'text-accent-purple' },
      { label: 'Ready', value: stats.ready, color: 'text-accent-teal' },
      { label: 'Revenue', value: null, color: 'text-success', display: formatCurrency(stats.totalRevenueCents, stats.currency) },
    ] : []),
  ]
  return (
    <div className="flex gap-4 overflow-x-auto pb-2">
      {tiles.map(s => (
        <div key={s.label} className="shrink-0 text-center">
          <p className={cn('text-lg font-bold', s.color)}>{s.display ?? s.value}</p>
          <p className="text-xs text-muted-foreground">{s.label}</p>
        </div>
      ))}
    </div>
  )
}

// ── Queue Stats Row ──────────────────────────────────────────────────

/** SA-285: clickable open-mission counters; hidden entirely when the
 * deployment has no open missions (non-quote deployments see nothing). */
function QueueStatsRow({
  queue, onSelect,
}: {
  queue: OrderQueueStats
  onSelect: (state: string) => void
}) {
  if (!queue.total) return null
  return (
    <div className="flex gap-2 overflow-x-auto pb-2">
      {Object.entries(QUEUE_STATE_CONFIG).map(([state, config]) => {
        const count = queue.byState[state] ?? 0
        if (!count) return null
        return (
          <button
            key={state}
            type="button"
            onClick={() => onSelect(state)}
            className={cn(
              'shrink-0 rounded-md border border-border px-3 py-1.5 text-center',
              'hover:bg-muted transition-colors',
            )}
          >
            <p className="text-lg font-bold text-foreground">{count}</p>
            <p className={cn('text-xs', config.className)}>{config.label}</p>
          </button>
        )
      })}
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────

/** SA-1338: how long a URL this page wrote is remembered as its own. The
 *  router commits a write as a transition, so its `searchParams` change can
 *  arrive a render or two after the write. A write a later one overtook is
 *  dropped as soon as the later write arrives (`live.slice(i + 1)`); expiry
 *  only catches a write that never arrives and that no arriving own write
 *  follows (for example a push to the URL already shown). */
const OWN_WRITE_MS = 2000

export function OrdersPage() {
  // SA-1338: Done on a task opened from a record comes back to that record's entry; replace it with the landing.
  useBackLanding()
  const [searchParams, setSearchParams] = useSearchParams()

  // Read URL-seeded filters ONCE, synchronously, before the hook call —
  // used as lazy useState initializers inside useOrders. This makes the
  // very first fetch already carry the filter, instead of an unfiltered
  // mount-effect fetch racing a later filtered refetch (the race let a
  // slow unfiltered response silently overwrite filtered results while
  // the "Filtered to one customer" chip stayed on screen).
  // SA-1183: the module views' own sort keys, read from the URL at mount (static, never store-gated).
  const moduleSortKeys = useMemo(() => moduleSorts(), [])
  // SA-1338: one reader of the list the URL means (ordersListUrl.listFromUrl).
  const initialList = listFromUrl(searchParams, moduleSortKeys)

  const {
    orders, total, capped, currentOrder, stats, isLoading, error,
    statusFilter, searchQuery, contactId, queueStateFilter, waitingOnFilter, stepFilter, commercialStatusFilter,
    quoteStatusFilter, processStateFilter, includeRejectedFilter, sortBy, sortDir,
    page,
    fetchOrders, fetchOrderDetail, fetchStats, createOrder, updateOrder,
    updateStatus, enterQuote, updateNotes, recordPayment, closeOutcome, reinstateRecord,
    getFeasibility, recordFeasibility, selectScenario, getAssignees,
    updateQueueState, assignOrder, getOrderEvents, getOrderMeasures, resolveDuplicate,
    getOrderGates, updateGateState,
    getIntakeAgendas, extractIntake, createQuoteRecord, getIntake, updateIntake, linkIntakeCompany,
    getCustomerUpdates, extractCustomerUpdate, applyCustomerUpdate, dismissCustomerUpdate,
    setStatusFilter, setSearchQuery, setContactId, setQueueStateFilter, setWaitingOnFilter, setStepFilter,
    setCommercialStatusFilter, setQuoteStatusFilter, setProcessStateFilter, setIncludeRejectedFilter, setSortBy, setSort, setPage,
    setCurrentOrder,
  } = useOrders({
    contactId: initialList.contactId, searchQuery: initialList.search,
    commercialStatus: initialList.commercialStatus,
    quoteStatus: initialList.quoteStatus, processState: initialList.processState, waitingOn: initialList.waitingOn,
    sort: searchParams.get('sort'), sortDir: searchParams.get('dir'), moduleSorts: moduleSortKeys,
  })

  // SA-401 D29: Start process is a staff action (tasks:work — the manager
  // recipe), no longer the workflows:update admin backfill.
  const canStartProcess = useCanAccessFeature('process_engine', 'tasks:work')
  // E4 (SA-389, spec D26): the page rides workflow_engine under the pack's
  // noun; payment, production and delivery UI is commerce.
  const showCommerce = useFeature('commerce')
  const noun = useRecordNoun()
  // ?panel=<section id> deep-links to that section (spec §7.5): the record
  // lane opens, rings and scrolls it; the form frame knows `feasibility` only.
  const focusPanel = searchParams.get('panel')
  // E4 (SA-397, spec D24): the register and the seat view exist on a
  // process_engine deployment. Strict (loaded AND on), like the record
  // panel: everything behind it is React Query and must not mount early.
  const processEnabled = useFeatureStore((s) => s.isLoaded && s.features.has('process_engine'))
  const viewParam = searchParams.get('view')
  const view = processEnabled && viewParam === 'tasks' ? 'tasks' as const : 'register' as const
  // E5 (spec §7.2, D56): `?view=<id>` also names a custom module's register
  // view. The adapter reads two stores and no query, so this page still calls
  // no React Query hook; the view's own component may. Any other value of
  // `view` is the register. SA-1184: offered as a toolbar toggle, never a tab.
  const registerViews = useRegisterViews()
  const manifestView = processEnabled ? registerViews.find((v) => v.id === viewParam) ?? null : null
  const [taskScope, setTaskScope] = useState<TaskScope>('mine')

  const [showNewOrder, setShowNewOrder] = useState(false)
  const [showEditOrder, setShowEditOrder] = useState(false)
  const [showNewQuote, setShowNewQuote] = useState(false)
  // SA-401: the New quote record entry exists only where an eligible intake
  // agenda is published (compass: shipment-quote). Fetched once; a failure
  // (403, feature off) simply leaves the button out. `null` until it answers,
  // `'failed'` when it could not — the register below tells the two apart.
  const [intakeAgendas, setIntakeAgendas] = useState<IntakeAgenda[] | 'failed' | null>(null)
  const agendas = Array.isArray(intakeAgendas) ? intakeAgendas : []
  // SA-401: bumped whenever the Intake panel saves a change while a process
  // is running, so RecordProcessBand (React Query, no shared client with
  // the Orders lane) knows to refetch.
  const [processRefreshToken, setProcessRefreshToken] = useState(0)
  // SA-1029 (spec §D20): the open record refetches on its own customer-update
  // event — this lane is hand-rolled, so the React Query invalidation the
  // shell does cannot reach it.
  // SA-1338: it follows the SHOWN record — a late answer after Back can leave `currentOrder` set but hidden.
  const openId = currentOrder && currentOrder.id === searchParams.get('order') ? currentOrder.id : null
  useEffect(() => {
    if (!openId) return
    return subscribeInboxEvents((e) => {
      if ((e.type === 'record_update_received' || e.type === 'record_update_settled') && e.orderId === openId) {
        void fetchOrderDetail(openId)
        setProcessRefreshToken((n) => n + 1)
      }
    })
  }, [openId, fetchOrderDetail])
  // SA-270: id → display name, for the Queue column's assignee initials.
  const [assigneeNames, setAssigneeNames] = useState<Record<string, string>>({})
  // SA-462 (spec §8.1–8.2): the Filters disclosure, open or not; the row
  // last opened, marked on return (D15) — state only, never persisted.
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [markedRowId, setMarkedRowId] = useState<string | null>(null)

  useEffect(() => { fetchOrders(); fetchStats() }, [fetchOrders, fetchStats])

  // The register is the table of a deployment that runs quote records through
  // a process: an intake agenda answered, and since SA-446 that list is exactly
  // the quote agendas a published process binds. The deployment decides, never
  // the page of rows on screen, so paging cannot flip the table (SA-435). Only
  // a failed read falls back to the rows. An active register filter keeps it
  // (and its filters) up when it matches nothing, so it can be cleared.
  // Everything else renders today's table (spec D24).
  const runsQuoteProcess = Array.isArray(intakeAgendas)
    ? intakeAgendas.length > 0
    : intakeAgendas === 'failed' && orders.some(o => o.orderType === 'quote')
  const registerMode = processEnabled
    && (runsQuoteProcess || !!stepFilter || !!commercialStatusFilter || !!quoteStatusFilter || !!processStateFilter || !!waitingOnFilter || includeRejectedFilter)
  // Until the read answers, a process_engine page cannot know its table: the
  // list waits on the spinner rather than paint one table and swap it.
  const tableUndecided = processEnabled && intakeAgendas === null
  const listReady = !isLoading && !tableUndecided
  // A manifest view is the register's rows in another table: it is offered,
  // and mounted, only where the register itself renders.
  const customView = registerMode ? manifestView : null
  // Step options: every step this page has seen a record at (the register
  // can span templates, so there is no single definition to list). The
  // current rows are merged in at render, so a fresh page offers its steps
  // at once; the remembered ones keep a filtered-away step selectable.
  const [knownSteps, setKnownSteps] = useState<Record<string, StepOption>>({})
  const stepOptions = useMemo(() => {
    const all = { ...knownSteps }
    for (const o of orders) {
      const step = o.process?.currentStep
      // The lane is the step's seat word (SA-462); the executor colours its dot (SA-468).
      if (step) all[step.id] = {
        id: step.id, number: step.number, title: step.title, lane: step.seatLabel ?? step.seat ?? null,
        executorType: step.executorType ?? null, level: step.level ?? null,
      }
    }
    return Object.values(all).sort((a, b) => a.number - b.number)
  }, [knownSteps, orders])
  useEffect(() => {
    setKnownSteps(prev => {
      const missing = stepOptions.filter(step => !prev[step.id])
      return missing.length ? { ...prev, ...Object.fromEntries(missing.map(step => [step.id, step])) } : prev
    })
  }, [stepOptions])

  useEffect(() => {
    let cancelled = false
    getIntakeAgendas().then((a) => { if (!cancelled) setIntakeAgendas(a) }).catch(() => { if (!cancelled) setIntakeAgendas('failed') })
    return () => { cancelled = true }
  }, [getIntakeAgendas])

  useEffect(() => {
    // getAssignees() never rejects (best-effort, like fetchStats) — a
    // failure just means initials stay hidden, not a page-level error.
    getAssignees().then(items => setAssigneeNames(Object.fromEntries(items.map(a => [a.id, a.name]))))
  }, [getAssignees])

  // SA-1338: the URLs this page wrote itself, oldest first, each kept
  // OWN_WRITE_MS. Every write the page makes notes its URL BEFORE calling
  // `setSearchParams`, so the effect below can tell its own writes from a
  // navigation it did not make.
  const ownWrites = useRef<{ url: string; at: number }[]>([])
  const noteOwnWrite = useCallback((next: URLSearchParams) => {
    ownWrites.current.push({ url: next.toString(), at: Date.now() })
  }, [])

  // SA-1338: a navigation the page did not make (the Views control's links,
  // the sidebar's entry, the browser's Back and Forward, a widget's link)
  // sets the list's parameters to what the URL says, through the plain
  // setters, only where they differ from the state (each returns to page 1,
  // so an unchanged list refetches nothing). The page's own writes never
  // re-seed: the box writes `?search=` as it types, and re-reading a write
  // that arrives a transition late would revert the typing after it (SA-1192,
  // final review, finding 1). A URL that matches one the page wrote is its
  // own; the writes before it are spent, the ones after it still on their way.
  // Any other URL is foreign and forgets them all. The mount needs nothing:
  // the state was seeded from the same URL above.
  useEffect(() => {
    const now = Date.now()
    const live = ownWrites.current.filter((w) => now - w.at <= OWN_WRITE_MS)
    const i = live.findIndex((w) => w.url === searchParams.toString())
    const own = i >= 0
    ownWrites.current = own ? live.slice(i + 1) : []
    if (!own) {
      const url = listFromUrl(searchParams, moduleSortKeys)
      if (url.search !== searchQuery) setSearchQuery(url.search)
      if (url.contactId !== contactId) setContactId(url.contactId)
      if (url.commercialStatus !== commercialStatusFilter) setCommercialStatusFilter(url.commercialStatus)
      if (url.quoteStatus !== quoteStatusFilter) setQuoteStatusFilter(url.quoteStatus)
      if (url.processState !== processStateFilter) setProcessStateFilter(url.processState)
      if (url.waitingOn !== waitingOnFilter) setWaitingOnFilter(url.waitingOn)
      if (url.sortBy !== sortBy || url.sortDir !== sortDir) {
        const column = columnSort(url.sortBy, url.sortDir, moduleSortKeys)
        if (column) setSort(column)
        else setSortBy(url.sortBy)
      }
    }
    // SA-1338: the record follows `order`. A URL that names a record opens it
    // and marks its row, as a row click does, so the row is marked after Back.
    // A URL that no longer names one (the browser's Back from a record, a link
    // to the list) closes the record as the in-page Back does, minus the URL write.
    const orderId = searchParams.get('order')
    if (orderId && (!currentOrder || currentOrder.id !== orderId)) {
      setMarkedRowId(orderId)
      fetchOrderDetail(orderId)
    } else if (!orderId && currentOrder) {
      setCurrentOrder(null)
      fetchOrders({ silent: true }); fetchStats()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // Final review, finding A: react-router's functional `setSearchParams`
  // hands the updater the LAST RENDER's params, not the previous call's, so
  // several writes in one handler do not compose — the last navigate wins
  // and puts back what the others removed. Every URL write here is ONE call
  // over the render's params, and a write that would change nothing is not
  // made at all (no navigate, no history entry).
  // SA-1192 (final review, finding 1): the router commits a URL write as a
  // transition, so until it does the render's `searchParams` still hold the
  // URL before it. A write made inside that window (a keystroke after a
  // keystroke, a keystroke after a chip removal) builds on, and compares
  // against, the latest params this page wrote, never the render's: built on
  // the render's, a correction back to the committed value was skipped and a
  // removed filter came back.
  const latestParams = useRef(searchParams)
  useLayoutEffect(() => { latestParams.current = searchParams }, [searchParams])
  const writeUrlParams = useCallback((changes: Record<string, string | null>) => {
    const base = latestParams.current
    const next = new URLSearchParams(base)
    for (const [param, value] of Object.entries(changes)) {
      if (value) next.set(param, value)
      else next.delete(param)
    }
    if (next.toString() === base.toString()) return
    latestParams.current = next
    noteOwnWrite(next)
    setSearchParams(next, { replace: true })
  }, [setSearchParams, noteOwnWrite])

  // SA-1192 (ruling R2): the box writes `?search=` as it types, replace and
  // never push (typing adds no Back-button stop), so the URL is the list on a
  // reload, a shared link and either Back. An empty box drops the param.
  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q)
    writeUrlParams({ search: q || null })
  }, [setSearchQuery, writeUrlParams])

  // SA-1124: a header's sort, written to the URL so a record opened from a
  // sorted page returns to it; the default writes nothing.
  const handleSort = useCallback((next: RegisterSort | null) => {
    setSort(next)
    writeUrlParams({ sort: next ? next.key : null, dir: next ? next.dir : null })
  }, [setSort, writeUrlParams])
  // SA-1338: the Register / My tasks links reset the sort because their URLs
  // carry none (the follow effect above reads it); SA-1124's F1 effect, which
  // reset it on any later `?view=` change, is gone, so Back to a sorted entry
  // keeps that entry's sort.
  // SA-1338: the legacy table's Sort by writes `?sort=` as the headers do, so
  // the URL is the list; the default writes nothing.
  const handleLegacySort = useCallback((v: string) => {
    setSortBy(v)
    writeUrlParams({ sort: v === DEFAULT_SORT ? null : v, dir: null })
  }, [setSortBy, writeUrlParams])
  // Priority, the one sort no column names (spec R1).
  const handlePriority = useCallback((on: boolean) => {
    setSortBy(on ? 'priority' : 'recent')
    writeUrlParams({ sort: on ? 'priority' : null, dir: null })
  }, [setSortBy, writeUrlParams])
  // SA-1184 (ruling R2): a register view's toggle switches the table in place.
  // Filters, search and the page stay; a column sort stays only when the
  // table switched to sorts by that column, else it returns to the default
  // order (and so page 1). One URL write, replace.
  const handleViewToggle = useCallback((next: string | null) => {
    const targetKeys = next ? (registerViews.find((v) => v.id === next)?.sortKeys ?? []) : REGISTER_SORT_KEYS
    const keep = sortSurvives(sortBy, targetKeys, moduleSortKeys)
    if (!keep) setSort(null)
    writeUrlParams(keep ? { view: next } : { view: next, sort: null, dir: null })
  }, [registerViews, sortBy, setSort, writeUrlParams, moduleSortKeys])

  const handleClearContactFilter = useCallback(() => {
    setContactId(null)
    writeUrlParams({ contact_id: null })
  }, [setContactId, writeUrlParams])

  // Fix round 1, finding 2 + final review, finding A: a URL-seeded filter's
  // param follows its chip — replace, never push, so a filter change adds no
  // Back-button stop. Wraps a plain setter: clearing (`''`) drops the param,
  // a new value writes it, so Back/reload never re-apply a filter the person
  // removed or changed.
  const urlSeededSetter = useCallback((setter: (v: string) => void, param: string) => (v: string) => {
    setter(v)
    writeUrlParams({ [param]: v || null })
  }, [writeUrlParams])

  // SA-462: every chip clears through the setter its select called, so the
  // request shape is unchanged (each setter returns to page 1, SA-411 D10).
  const filters: RegisterFilterState = {
    step: stepFilter, commercialStatus: commercialStatusFilter, orderStatus: statusFilter, queueState: queueStateFilter, waitingOn: waitingOnFilter, contactId,
    quoteStatus: quoteStatusFilter, processState: processStateFilter, includeRejected: includeRejectedFilter,
  }
  const filterSetters = useMemo<RegisterFilterSetters>(() => ({
    step: setStepFilter,
    commercialStatus: urlSeededSetter(setCommercialStatusFilter, 'commercial_status'),
    orderStatus: setStatusFilter,
    queueState: setQueueStateFilter,
    waitingOn: urlSeededSetter(setWaitingOnFilter, 'waiting_on'),
    contact: (id) => { if (id) setContactId(id); else handleClearContactFilter() },
    quoteStatus: urlSeededSetter(setQuoteStatusFilter, 'quote_status'),
    processState: urlSeededSetter(setProcessStateFilter, 'process_state'),
    includeRejected: setIncludeRejectedFilter,
  }), [setStepFilter, setCommercialStatusFilter, setStatusFilter, setQueueStateFilter, setWaitingOnFilter, setContactId, handleClearContactFilter,
    setQuoteStatusFilter, setProcessStateFilter, setIncludeRejectedFilter, urlSeededSetter])
  // Clear all (finding A): state through the PLAIN setters, then ONE URL
  // write for every seeded param — the chip setters each write the URL, and
  // seven of those in one handler would not compose.
  const handleClearAll = useCallback(() => {
    clearAllFilters({
      step: setStepFilter, commercialStatus: setCommercialStatusFilter, orderStatus: setStatusFilter,
      queueState: setQueueStateFilter, waitingOn: setWaitingOnFilter, contact: setContactId, quoteStatus: setQuoteStatusFilter,
      processState: setProcessStateFilter, includeRejected: setIncludeRejectedFilter,
    })
    writeUrlParams({ contact_id: null, commercial_status: null, quote_status: null, process_state: null, waiting_on: null })
  }, [setStepFilter, setCommercialStatusFilter, setStatusFilter, setQueueStateFilter, setWaitingOnFilter, setContactId,
    setQuoteStatusFilter, setProcessStateFilter, setIncludeRejectedFilter, writeUrlParams])
  const contactName = contactId ? orders.find((o) => o.contactId === contactId)?.customerName ?? null : null

  // A record opened from a manifest view keeps that view in the URL, so Back
  // returns to the table the person was reading, not to the register.
  const keptView = manifestView?.id
  // SA-1124 / SA-1192: so do the list's search, its URL-seeded filters and its
  // sort (LIST_PARAMS), or a reload after Back would show another list. A value
  // is carried as it stands; an empty one is dropped, as `writeUrlParams` does,
  // and both read the latest params the page wrote (finding 1 above).
  const keptParams = useCallback((extra: Record<string, string>) => {
    const kept: Record<string, string> = keptView ? { view: keptView } : {}
    const params = latestParams.current
    for (const param of LIST_PARAMS) {
      const value = params.get(param)
      if (value) kept[param] = value
    }
    return { ...kept, ...extra }
  }, [keptView])
  // SA-1338: both note the URL they write, so the follow effect knows it.
  const handleSelectOrder = useCallback((order: Order) => {
    setMarkedRowId(order.id)
    fetchOrderDetail(order.id)
    const next = new URLSearchParams(keptParams({ order: order.id }))
    noteOwnWrite(next)
    setSearchParams(next)
  }, [fetchOrderDetail, setSearchParams, keptParams, noteOwnWrite])

  const handleBack = useCallback(() => {
    setCurrentOrder(null)
    const next = new URLSearchParams(keptParams({}))
    noteOwnWrite(next)
    setSearchParams(next)
    fetchOrders({ silent: true }); fetchStats()
  }, [setCurrentOrder, setSearchParams, fetchOrders, fetchStats, keptParams, noteOwnWrite])

  const handleUpdateStatus = useCallback(async (status: string) => {
    if (!currentOrder) return
    await updateStatus(currentOrder.id, status)
    // SA-478: Cancel closes a running process, so the band and the record's process sections re-read.
    setProcessRefreshToken((n) => n + 1)
  }, [currentOrder, updateStatus])

  const handleEnterQuote = useCallback(async (amountCents: number, notes?: string) => {
    if (!currentOrder) return
    await enterQuote(currentOrder.id, amountCents, notes)
  }, [currentOrder, enterQuote])

  const handleUpdateNotes = useCallback(async (notes: string) => {
    if (!currentOrder) return
    await updateNotes(currentOrder.id, notes)
  }, [currentOrder, updateNotes])

  const handleRecordPayment = useCallback(async (amountCents: number, method: string) => {
    if (!currentOrder) return
    await recordPayment(currentOrder.id, { amountCents, method })
  }, [currentOrder, recordPayment])

  const handleCloseOutcome = useCallback(async (outcome: QuoteOutcome, note?: string) => {
    if (!currentOrder) return
    await closeOutcome(currentOrder.id, { outcome, note })
    // SA-478: a close closes the process too, so the band and the record's process sections re-read.
    setProcessRefreshToken((n) => n + 1)
  }, [currentOrder, closeOutcome])

  // SA-785: the band shows the re-entered step, and the record counts again.
  const handleReinstate = useCallback(async () => {
    if (!currentOrder) return
    await reinstateRecord(currentOrder.id)
    setProcessRefreshToken((n) => n + 1)
    fetchStats()
  }, [currentOrder, reinstateRecord, fetchStats])

  const handleSaveOrder = useCallback(async (orderId: string, payload: OrderUpdatePayload) => {
    await updateOrder(orderId, payload)
  }, [updateOrder])

  // updateQueueState/assignOrder already push the refreshed order into
  // currentOrder/orders (useOrders' own setCurrentOrder/setOrders calls) —
  // this callback only needs to refresh the aggregate counts.
  const handleQueueChanged = useCallback(() => {
    fetchStats()
  }, [fetchStats])

  // Detail view. SA-1338: a record shows only while the URL names it.
  // `fetchOrderDetail` has no staleness guard and the write handlers in
  // `useOrders` set the record unconditionally, so an answer that lands after
  // a browser Back (or under another record's address) would otherwise bring
  // the record back. Every handler keeps reading `currentOrder`.
  if (currentOrder && currentOrder.id === searchParams.get('order')) {
    return (
      <>
        <OrderDetail
          order={currentOrder}
          onBack={handleBack}
          onUpdateStatus={handleUpdateStatus}
          onEnterQuote={handleEnterQuote}
          onUpdateNotes={handleUpdateNotes}
          onRecordPayment={handleRecordPayment}
          onCloseOutcome={handleCloseOutcome}
          onReinstate={handleReinstate}
          onEdit={() => setShowEditOrder(true)}
          getFeasibility={getFeasibility}
          recordFeasibility={recordFeasibility}
          selectScenario={selectScenario}
          onQueueChanged={handleQueueChanged}
          updateQueueState={updateQueueState}
          assignOrder={assignOrder}
          getOrderEvents={getOrderEvents}
          getOrderMeasures={getOrderMeasures}
          getAssignees={getAssignees}
          resolveDuplicate={resolveDuplicate}
          getOrderGates={getOrderGates}
          updateGateState={updateGateState}
          canStartProcess={canStartProcess}
          showCommerce={showCommerce}
          noun={noun}
          focusPanel={focusPanel}
          onRecordChanged={() => { void fetchOrderDetail(currentOrder.id); setProcessRefreshToken((n) => n + 1) }}
          getIntake={getIntake}
          updateIntake={updateIntake}
          linkIntakeCompany={linkIntakeCompany}
          extractIntake={extractIntake}
          processRefreshToken={processRefreshToken}
          getCustomerUpdates={getCustomerUpdates}
          extractCustomerUpdate={extractCustomerUpdate}
          applyCustomerUpdate={applyCustomerUpdate}
          dismissCustomerUpdate={dismissCustomerUpdate}
          onIntakeChanged={() => { void fetchOrderDetail(currentOrder.id); setProcessRefreshToken((n) => n + 1) }}
          assigneeNames={assigneeNames}
        />
        <NewOrderModal
          open={showEditOrder}
          onOpenChange={setShowEditOrder}
          order={currentOrder}
          onSave={handleSaveOrder}
          onRecordPayment={recordPayment}
          commerceEnabled={showCommerce}
        />
      </>
    )
  }

  // List view. The built-in register and a manifest view take the same props:
  // the same page of rows, under the same filters.
  const registerProps = {
    orders, onSelect: handleSelectOrder, noun, assigneeNames,
    showStatus: orders.some(o => o.commercialStatus) || sortBy === 'status',
    markedRowId,
    sort: columnSort(sortBy, sortDir, moduleSortKeys), onSort: handleSort,
    // SA-462 (spec §8.3): the pager is the card's footer on the register lane.
    footer: <ListPager page={page} pageSize={ORDERS_PAGE_SIZE} total={total} onPage={setPage} />,
  }
  return (
    <PageFrame width="list">
      <PageHeader
        title={noun.plural}
        icon={showCommerce ? ShoppingCart : ClipboardList}
        sticky
        subtitle={!registerMode && !tableUndecided && `${total} ${(total === 1 ? noun.singular : noun.plural).toLowerCase()}`}
        actions={
          <>
            {/* POST /orders takes catalog items (min one), so it is commerce
                end to end (E4, spec D26); the record lane births through
                "New quote record" below. */}
            {showCommerce && (
              <button onClick={() => setShowNewOrder(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:opacity-90">
                <Plus className="w-4 h-4" /> New {noun.singular.toLowerCase()}
              </button>
            )}
            {agendas.length > 0 && (
              <Button variant="outline" size="sm" onClick={() => setShowNewQuote(true)} {...tourAnchor('records.header.new-quote')}>
                <Plus aria-hidden="true" className="w-4 h-4" /> New quote record
              </Button>
            )}
            <Button variant="ghost" size="sm" aria-label={`Refresh ${noun.plural.toLowerCase()}`}
              onClick={() => { fetchOrders(); fetchStats() }} className="px-2">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </>
        }
      >
        {processEnabled && (view === 'tasks' || !registerMode) && (
          <RecordViewSwitch view={view} />
        )}

        {view === 'tasks' ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Open tasks on these {noun.plural.toLowerCase()}, by due date.</p>
            <TaskScopeSwitch scope={taskScope} onChange={setTaskScope} />
          </div>
        ) : registerMode ? (<>
        {/* SA-462 (spec §8.1, D13): the register lane's toolbar; the stats bar
            and the queue tiles fold into the Filters panel's chips. */}
        <RegisterToolbar
          viewSwitch={<RecordViewSwitch view={view} />}
          noun={noun} searchQuery={searchQuery} onSearch={handleSearch}
          filters={filters} setters={filterSetters} steps={stepOptions} contactName={contactName}
          filtersOpen={filtersOpen} onToggleFilters={() => setFiltersOpen((o) => !o)} panelId="register-filters"
          showing={orders.length} total={total} priority={sortBy === 'priority'} onPriority={handlePriority}
          viewToggles={registerViews} activeView={customView?.id ?? null} onViewToggle={handleViewToggle}
        />
        {filtersOpen && (
          <RegisterFilterPanel id="register-filters" steps={stepOptions} filters={filters} setters={filterSetters}
            queueCounts={stats?.queue.byState ?? null} showCommerce={showCommerce}
            onClearAll={handleClearAll} />
        )}
        {capped && sortBy === 'priority' && (
          <p className="text-xs text-warning-strong">
            Ranking computed over the newest 500 {noun.plural.toLowerCase()} — older ones
            are not scored.
          </p>
        )}
        </>) : (<>
        {/* Stats */}
        <StatsBar stats={stats} showCommerce={showCommerce} />
        {stats && (
          <QueueStatsRow
            queue={stats.queue}
            onSelect={(state) => setQueueStateFilter(state)}
          />
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text" placeholder={`Search ${noun.plural.toLowerCase()}...`}
              value={searchQuery} onChange={e => handleSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-background"
            />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-border bg-background">
            <option value="">All Statuses</option>
            {Object.entries(STATUS_CONFIG)
              .filter(([k]) => showCommerce || !COMMERCE_LADDER_STATUSES.has(k as OrderStatus))
              .map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
          </select>
          <select aria-label="Queue state" value={queueStateFilter} onChange={e => setQueueStateFilter(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-border bg-background">
            <option value="">Any queue state</option>
            {Object.entries(QUEUE_STATE_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          {contactId && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full bg-info/10 text-info">
              Filtered to one customer
              <button
                type="button"
                aria-label="Clear contact filter"
                onClick={handleClearContactFilter}
                className="p-0.5 rounded-full hover:bg-info/20"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
          <select aria-label="Sort by" value={sortBy} onChange={e => handleLegacySort(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-border bg-background">
            <option value="recent">Most Recent</option>
            <option value="oldest">Oldest First</option>
            <option value="amount">Highest Amount</option>
            <option value="delivery_date">Delivery Date</option>
            <option value="priority">Priority</option>
          </select>
          {capped && sortBy === 'priority' && (
            <p className="text-xs text-warning-strong">
              Ranking computed over the newest 500 {noun.plural.toLowerCase()} — older ones
              are not scored.
            </p>
          )}
        </div>
        </>)}
      </PageHeader>

      {showCommerce && (
      <NewOrderModal
        open={showNewOrder}
        onOpenChange={setShowNewOrder}
        onCreate={async (payload) => {
          const order = await createOrder(payload)
          // Select the created order the same way a list-row click does,
          // so staff land on it directly — an active contact_id filter
          // would otherwise silently exclude a fresh manual order from
          // the refreshed list, leaving no visible confirmation.
          handleSelectOrder(order)
        }}
      />
      )}

      {agendas.length > 0 && (
        <NewQuoteRecordModal
          open={showNewQuote}
          onOpenChange={setShowNewQuote}
          agendas={agendas}
          onExtract={extractIntake}
          onCreate={async (payload) => {
            const order = await createQuoteRecord(payload)
            handleSelectOrder(order)
          }}
        />
      )}

      {view === 'tasks' ? (
        <div className="py-4 space-y-4"><TasksList scope={taskScope} /></div>
      ) : (
      <div className="py-4 space-y-4" {...tourAnchor('records.register.table')}>
      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Loading */}
      {(isLoading || tableUndecided) && (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      )}

      {/* Order list */}
      {listReady && orders.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No {noun.plural.toLowerCase()} found</p>
        </div>
      )}

      {listReady && orders.length > 0 && (registerMode ? (
        customView
          ? <RegisterViewSlot entry={customView} {...registerProps} />
          : <RegisterColumnsSlot {...registerProps} />
      ) : (
        <LegacyOrdersTable orders={orders} onSelect={handleSelectOrder} assigneeNames={assigneeNames} />
      ))}
      {/* SA-411: one pager under the legacy table; hidden at one page. On the
          register lane it is the table card's footer (SA-462). */}
      {listReady && orders.length > 0 && !registerMode && (
        <ListPager page={page} pageSize={ORDERS_PAGE_SIZE} total={total} onPage={setPage} />
      )}
      </div>
      )}
    </PageFrame>
  )
}

export default OrdersPage
